package com.devi3east.bashbuddy.search.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.events.common.data.models.Event
import com.devi3east.bashbuddy.features.events.feed.ui.components.EventCard
import com.devi3east.bashbuddy.search.ui.components.EmptySearchContent
import com.devi3east.bashbuddy.search.ui.components.FilterBar
import com.devi3east.bashbuddy.search.ui.components.SearchBar
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun SearchRoute(
    appState: AppState,
    navigateToEventDetail: (String, String) -> Unit
) {
    val viewModel: SearchViewModel = koinViewModel()
    val uiState = viewModel.uiState.collectAsState()
    val query = viewModel.query.collectAsState()
    val filters = viewModel.filters.collectAsState()
    val availableStates = viewModel.availableStates.collectAsState(initial = emptyList())

    LaunchedEffect(true) {
        viewModel.getEventsList()
    }

    SearchScreen(
        appState = appState,
        events = uiState.value.eventsList,
        isLoading = uiState.value.isLoading,
        errorMessage = uiState.value.errorMessage,
        searchQuery = query.value,
        filters = filters.value,
        availableStates = availableStates.value,
        onQueryChanged = viewModel::onSearchQueryChanged,
        onFiltersChanged = viewModel::updateFilters,
        onClearFilters = viewModel::clearFiltersAndQuery,
        navigateToEventDetail = navigateToEventDetail
    )
}

@Composable
fun SearchScreen(
    appState: AppState,
    events: List<Event>,
    isLoading: Boolean,
    errorMessage: String?,
    searchQuery: String,
    filters: SearchFilters,
    availableStates: List<String>,
    onQueryChanged: (String) -> Unit,
    onFiltersChanged: (SearchFilters) -> Unit,
    onClearFilters: () -> Unit,
    navigateToEventDetail: (String, String) -> Unit
) {
    val placeholder = when (filters.filterType) {
        SearchFilterType.EVENT_TITLE -> "Start typing an event..."
        SearchFilterType.ORGANIZATION -> "Start typing an organization..."
        else -> "Start typing..."
    }

    Scaffold(
        topBar = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(MaterialTheme.colorScheme.background)
                    .padding(horizontal = 12.dp)
            ) {
                SearchBar(
                    query = searchQuery,
                    onQueryChanged = onQueryChanged,
                    placeholder = placeholder
                )
                FilterBar(
                    filters = filters,
                    availableStates = availableStates,
                    onFiltersChanged = onFiltersChanged,
                    onClearFilters = onClearFilters
                )
            }
        }
    ) { innerPadding ->

        when {
            isLoading -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }
            }

            errorMessage != null -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    Text(text = errorMessage)
                }
            }

            events.isEmpty() -> {
                EmptySearchContent()
            }

            else -> {
                BoxWithConstraints(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(top = innerPadding.calculateTopPadding())
                ) {
                    val screenWidth = maxWidth

                    val columns = when {
                        screenWidth < 600.dp -> 2
                        screenWidth <= 900.dp -> 3
                        else -> 4
                    }

                    val horizontalPadding = when {
                        screenWidth < 600.dp -> 0.dp
                        screenWidth <= 900.dp -> 48.dp
                        else -> 96.dp
                    }

                    LazyVerticalGrid(
                        columns = GridCells.Fixed(columns),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        contentPadding = PaddingValues(
                            start = horizontalPadding,
                            end = horizontalPadding,
                            top = 4.dp,
                            bottom = 24.dp
                        ),
                        modifier = Modifier.fillMaxSize()
                    ) {
                        itemsIndexed(events, key = { _, it -> it._id }) { index, event ->

                            val colIndex = index % columns
                            val startPadding = if (colIndex == 0) 16.dp else 0.dp
                            val endPadding = if (colIndex == columns - 1) 16.dp else 0.dp

                            EventCard(
                                event = event,
                                onClick = {
                                    navigateToEventDetail(event._id, event.slug)
                                },
                                modifier = Modifier.padding(
                                    start = startPadding,
                                    end = endPadding
                                )
                            )
                        }
                    }
                }
            }
        }
    }
}